import { useEffect, useState, useMemo } from "react";
import EmployeeLayout from "../components/EmployeeLayout";
import api from "../api";

const EmpDashboard = () => {
  const [entries, setEntries] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadingBdays, setLoadingBdays] = useState(false);
  const [newsletters, setNewsletters] = useState([]);
  const [loadingNews, setLoadingNews] = useState(false);

  useEffect(() => {
    loadEntries();
    loadBirthdays();
    loadNewsletters();
  }, []);

  const loadEntries = async () => {
    setLoadingEntries(true);
    try {
      const res = await api.get("/daily-sheet");
      setEntries(res.data || []);
    } catch (err) {
      console.error("Error loading entries", err);
      setEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  };

  const loadBirthdays = async () => {
    setLoadingBdays(true);
    try {
      const res = await api.get("/employee/birthdays");
      setBirthdays(res.data || []);
    } catch (err) {
      console.error("Error loading birthdays", err);
      setBirthdays([]);
    } finally {
      setLoadingBdays(false);
    }
  };

  const loadNewsletters = async () => {
    setLoadingNews(true);
    try {
      const res = await api.get("/employee/newsletters");
      setNewsletters(res.data || []);
    } catch (err) {
      console.error("Error loading newsletters", err);
      // If no endpoint yet, fall back to sample data
      setNewsletters([
        {
          id: 1,
          title: "Welcome to QDesk",
          body: "Stay tuned for weekly updates, events, and highlights.",
          date: new Date().toISOString().slice(0, 10),
        },
      ]);
    } finally {
      setLoadingNews(false);
    }
  };

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const missingDates = useMemo(() => {
    const set = new Set(
      (entries || []).map((e) => (e.entry_date || "").slice(0, 10))
    );
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today);
    end.setDate(end.getDate() - 1); // up to yesterday

    if (end < start) return [];

    const missing = [];
    for (
      let d = new Date(start);
      d <= end;
      d.setDate(d.getDate() + 1)
    ) {
      const iso = d.toISOString().slice(0, 10);
      if (!set.has(iso)) missing.push(iso);
    }
    return missing;
  }, [entries, today]);

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <EmployeeLayout>
      <div className="emp-header emp-hero">
        <div>
          <h1 className="emp-title">Your Day at a Glance</h1>
          <p className="emp-subtitle">
            Quick reminders, announcements, and celebrations curated for you.
          </p>
        </div>
      </div>

      {missingDates.length > 0 && (
        <div className="alert-warning dash-block">
          <div className="dash-block-icon">⚠️</div>
          <div>
            <strong>Heads up:</strong> You’ve missed filling the daily sheet for{" "}
            {missingDates.length} day(s) this month (up to yesterday). Please catch up to stay compliant.
          </div>
        </div>
      )}

      <div className="emp-dashboard-grid">
        <div className="dash-card">
          <div className="dash-card-header">
            <div>
              <h3>Newsletter</h3>
              <p className="text-muted">
                Latest updates and announcements from the admin team.
              </p>
            </div>
            <span className="dash-pill">Updates</span>
          </div>
          {loadingNews ? (
            <p>Loading...</p>
          ) : newsletters.length === 0 ? (
            <p className="text-muted">No newsletter messages yet.</p>
          ) : (
            <div className="newsletter-stack">
              {newsletters.map((n) => (
                <div key={n.id} className="newsletter-card">
                  <div className="newsletter-card-top">
                    <span className="newsletter-date-pill">
                      {formatDate(n.created_at || n.date)}
                    </span>
                    <span className="newsletter-badge">News</span>
                  </div>
                  <h4 className="newsletter-title-rich">{n.title}</h4>
                  <p className="newsletter-body-rich">{n.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dash-card">
          <div className="dash-card-header">
            <div>
              <h3>Upcoming Birthdays (This Month)</h3>
              <p className="text-muted">
                From today through the end of this month.
              </p>
            </div>
            <span className="dash-pill">🎂</span>
          </div>
          {loadingBdays ? (
            <p>Loading...</p>
          ) : birthdays.length === 0 ? (
            <p className="text-muted">No birthdays remaining this month.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="emp-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Name</th>
                    <th>Emp Code</th>
                  </tr>
                </thead>
                <tbody>
                  {birthdays.map((b) => (
                    <tr key={b.id}>
                      <td>{String(b.day).padStart(2, "0")}</td>
                      <td>
                        {b.first_name} {b.last_name}
                      </td>
                      <td>{b.emp_code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="dash-card">
        <div className="card-header">
          <h3>Daily Sheet Reminder</h3>
          <p className="text-muted" style={{ marginTop: 4 }}>
            Submit today’s entry to avoid missing your log.
          </p>
        </div>
        <div className="alert-info dash-block">
          <div className="dash-block-icon">🗒️</div>
          <div>
            Fill your daily sheet for today and pending days to keep your manager updated.
          </div>
        </div>
      </div>
    </EmployeeLayout>
  );
};

export default EmpDashboard;
